import Pusher, { type Channel } from 'pusher-js';
import { Md5 } from 'ts-md5';
import { browser } from '$app/environment';
import { env } from '$env/dynamic/public';

// App-wide channel shared with the API (see PusherClient.primary_qualifier and
// Shared::Banner in flowapi). The key must hash the same string the API does:
// application/fli/<PRIMARY_QUALIFIER>.
export function applicationChannelName(): string {
  return Md5.hashStr(`application/fli/${env.PUBLIC_PRIMARY_QUALIFIER}`);
}

// Thin wrapper around pusher-js. The connection is opened lazily on the first
// subscription and never on the server. Without a configured key, or without
// the qualifier that names the channel, the service is disabled and
// subscriptions resolve to null, so callers degrade to the fetch-on-load
// behaviour instead of listening on a channel the API never publishes to.
class PusherService {
  private client?: Pusher;

  get enabled(): boolean {
    return browser && !!env.PUBLIC_PUSHER_KEY && !!env.PUBLIC_PRIMARY_QUALIFIER;
  }

  subscribe(channelName: string): Channel | null {
    if (!this.enabled) return null;

    return this.pusher.subscribe(channelName);
  }

  unsubscribe(channelName: string): void {
    this.client?.unsubscribe(channelName);
  }

  applicationChannel(): Channel | null {
    return this.subscribe(applicationChannelName());
  }

  private get pusher(): Pusher {
    this.client ??= new Pusher(env.PUBLIC_PUSHER_KEY, { cluster: env.PUBLIC_PUSHER_CLUSTER });

    return this.client;
  }
}

export const pusherService = new PusherService();
